<template>
    <button
      class="button"
      :class="'button-' + type"
      :style="`width: ${width}; height: ${height};`"
      :disabled="loading || disabled"
    >
      <div v-if="loading">
        <div class="lds-ring" :style="preloader">
          <div :style="preloaderChildren"></div>
          <div :style="preloaderChildren"></div>
          <div :style="preloaderChildren"></div>
          <div :style="preloaderChildren"></div>
        </div>
      </div>

      <span v-else :style="styconstextPosition">
        <span v-if="title" class="content_item" >{{ title }}</span>
        <span class="play-icon" v-if="hasPlayIcon">
          <svg width="9" height="11" viewBox="0 0 9 11" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8.25 4.76314C8.91667 5.14804 8.91667 6.11029 8.25 6.49519L1.5 10.3923C0.833333 10.7772 -4.50056e-07 10.2961 -4.16407e-07 9.52628L-7.57103e-08 1.73205C-4.20613e-08 0.96225 0.833333 0.481125 1.5 0.866025L8.25 4.76314Z" fill="currentColor"/>
          </svg>
        </span>
      </span>
    </button>
</template>

<script>
export default {
  name: 'app-button',
  props: {
    title: String,
    type: String,
    width: {
      type: String,
      default: '160px'
    },
    height: {
      type: String,
      default: '40px'
    },
    textPosition: {
      type: String,
      default: 'right'
    },

    loading: {
      type: Boolean,
      default: false
    },
    hasPlayIcon: Boolean,
    disabled: Boolean
  },
  methods: {},
  computed: {
    styconstextPosition () {
      let direction
      switch (this.textPosition) {
        case 'top':
          direction = 'column'
          break
        case 'left':
          direction = 'row'
          break
        case 'right':
          direction = 'row-reverse'
          break
        case 'bottom':
          direction = 'column-reverse'
          break
      }
      return {
        display: 'flex',
        'align-items': 'center',
        'flex-direction': direction,
        'justify-content': 'center',
        height: '100%'
      }
    },

    preloader () {
      const height = this.height.replace(/\D+/g, '')
      const width = this.width.replace(/\D+/g, '')
      const pixel = +height < +width ? height : width
      return `height: ${pixel}px; width: ${pixel}px`
    },
    preloaderChildren () {
      const height = this.height.replace(/\D+/g, '')
      const width = this.width.replace(/\D+/g, '')
      const pixel = +height < +width ? height : width
      const border = +pixel / 16
      return `height: ${pixel - 20}px; width: ${pixel - 20}px; border: ${border}px solid; border-color: ${
        this.dark ? 'black' : 'white'
      } transparent transparent transparent`
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../../assets/css/system/_vars';
@import '../../../assets/css/system/_mixins';

.button {
  display: flex;
  justify-content: center;
  align-items: center;
  color: $font_color_alt;
  border-radius: 3px;
  padding: 10px 15px;
  transition: .3s;
  cursor: pointer;
  user-select: none;
  font-family: $font_main;
}

.button-main {
  background: $button_color;
  box-shadow: 0 2px 2px #C6523B;

  &[disabled] {
    background: $color_deactive;
    box-shadow: none;
    pointer-events: none;
  }

  &:hover {
    box-shadow: none;
  }

  &:active {
    box-shadow: inset 0px 4px 4px rgba(128, 37, 15, 0.54);
  }
}

.button-second {
  background: $button_color_second;
  box-shadow: 0px 2px 2px #4C095C;

  &[disabled] {
    background: $color_deactive;
    box-shadow: none;
    pointer-events: none;
  }

  &:hover {
    box-shadow: none;
  }

  &:active {
    box-shadow: inset 0 4px 4px rgba(44, 4, 48, 0.54);
  }
}

.button-third {
  background: $button_color_third;
  box-shadow: 0 2px 2px #13557C;

  &[disabled] {
    background: $color_deactive;
    box-shadow: none;
    pointer-events: none;
  }

  &:hover {
    box-shadow: none;
  }

  &:active {
    box-shadow: inset 0 4px 4px #0E476A;
  }
}

.button-fourth {
  background: $button_color_fourth;
  box-shadow: 0 2px 2px #557428;

  &[disabled] {
    background: $color_deactive;
    box-shadow: none;
    pointer-events: none;
  }

  &:hover {
    box-shadow: none;
  }

  &:active {
    box-shadow: inset 0 4px 4px #557428;
  }
}

.button-alt {
  background: $color_main;
  color: $font_color_second;
  border: 1px solid $font_color_second;
  box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.42);

  &[disabled] {
    color: $color_deactive;
    border: 1px solid $color_deactive;
    box-shadow: none;
    pointer-events: none;
  }

  &:hover {
    box-shadow: none;
  }

  &:active {
    box-shadow: inset 0px 4px 4px rgba(0, 0, 0, 0.45);
  }
}

.button-gray {
  background: $color_controls;
  box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.42);

  &[disabled] {
    color: $color_deactive;
    box-shadow: none;
    pointer-events: none;
  }

  &:hover {
    box-shadow: none;
  }

  &:active {
    box-shadow: inset 0px 4px 4px rgba(0, 0, 0, 0.45);
  }
}

.button-blue {
  border: 1px solid $color_second-border2;
  color: $color_second-border2;
  font-weight: bold;
  background: transparent;

  &[disabled] {
    border: 1px solid $color_deactive;
    color: $color_deactive;
    pointer-events: none;
  }

  &:hover {
    color: $font_color_alt;
    background: $color_second-border2;
  }
}

.play-icon {
  margin-left: 8px;
}

.lds-ring {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  width: 80px;
  height: 80px;
}
.lds-ring div {
  box-sizing: border-box;
  display: block;
  position: absolute;
  width: 64px;
  height: 64px;
  //border: 4px solid
  border-radius: 50%;
  animation: lds-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
  //border-color: white transparent transparent transparent
}

.lds-ring div:nth-child(1) {
  animation-delay: -0.45s;
}
.lds-ring div:nth-child(2) {
  animation-delay: -0.3s;
}
.lds-ring div:nth-child(3) {
  animation-delay: -0.15s;
}
@keyframes lds-ring {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
